import React from 'react';
import PropTypes from 'prop-types';
import {
	getNavItemKey,
	isVisibleNavItem,
	isFragmentWrapper,
	safeNavItems
} from '../utils/navItemUtils';
import DrilldownLeafLink from './DrilldownLeafLink';
import DrawerNavFragment from './DrawerNavFragment';
import NavItemLabel from './NavItemLabel';

/** BEM class for the drilldown section heading element. */
export const DRILLDOWN_HEADING_CLASS =
	'ws-nav-drawer__drilldown-section-heading';

/**
 * DrilldownGroup — renders a group heading followed by its visible child links
 * and any embedded fragment slots.
 *
 * @param {Object} props
 * @param {NavigationItem} props.group - A navigation item acting as a group heading
 * @param {() => void} props.onLinkClick - Called when a child link is clicked
 */
const DrilldownGroup = ({ group, onLinkClick }) => {
	const allChildren = safeNavItems(group).filter(
		(child) =>
			isVisibleNavItem(child) ||
			child.embedded ||
			isFragmentWrapper(child)
	);
	if (allChildren.length === 0) return null;

	// Pass 1: non-embedded children (leaf links + fragment-wrapper groups) → inside <ul>
	const nonEmbeddedChildren = allChildren.filter((child) => !child.embedded);
	// Pass 2: embedded children (fragment slots) → <div> elements AFTER the <ul>
	const embeddedChildren = allChildren.filter((child) => child.embedded);

	return (
		<li className="ws-nav-drawer__drilldown-group">
			{group.label?.trim() && (
				<strong
					className={`${DRILLDOWN_HEADING_CLASS} pt-6 pb-4 h4`}
					data-testid="drilldown-section-heading"
				>
					<NavItemLabel label={group.label} />
				</strong>
			)}
			{nonEmbeddedChildren.length > 0 && (
				<ul className="list-unstyled m-0">
					{nonEmbeddedChildren.map((child, index) =>
						isFragmentWrapper(child) ? (
							<DrilldownGroup
								key={getNavItemKey(child, index)}
								group={child}
								onLinkClick={onLinkClick}
							/>
						) : (
							<DrilldownLeafLink
								key={getNavItemKey(child, index)}
								item={child}
								onLinkClick={onLinkClick}
							/>
						)
					)}
				</ul>
			)}
			{embeddedChildren.map((child, index) => (
				<div
					key={getNavItemKey(child, index)}
					className="ws-nav-drawer__fragment"
				>
					<DrawerNavFragment url={child.url} />
				</div>
			))}
		</li>
	);
};

DrilldownGroup.propTypes = {
	group: PropTypes.shape({
		label: PropTypes.string,
		navigationItems: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	onLinkClick: PropTypes.func.isRequired
};

export default DrilldownGroup;
